import React, { useState, useEffect } from 'react';
import { 
  Database, RefreshCw, Download, Trash2, Calendar, 
  ShieldCheck, Clock, ArrowUpDown, Play, HardDrive,
  Settings as SettingsIcon, History, Loader2, Info, ChevronDown, Save, Zap, ChevronRight
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { callSiteApi } from '../utils';
import type { Site } from '../types';

interface Backup {
  id: string;
  name: string;
  lastModified: string;
  type: 'full' | 'incremental' | 'legacy';
  totalSize: number;
  files?: { key: string; size: number } | null;
  database?: { key: string; size: number } | null;
  legacy?: { key: string; size: number } | null;
}

interface BackupsTabProps {
  site: Site;
  onUpdateSite?: (updates: Partial<Site>) => void;
}

export const BackupsTab: React.FC<BackupsTabProps> = ({ site, onUpdateSite }) => {
  const { toast } = useToast();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [backupProgress, setBackupProgress] = useState<number>(site.backup_progress ?? 0);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Scheduling state — local draft values (not saved until "Save Settings")
  const [recurrence, setRecurrence] = useState(site.backupRecurrence || 'manual');
  const [retention, setRetention] = useState(site.backupRetention || 7);
  const [backupTime, setBackupTime] = useState(site.backupTime || '20:00:00');
  const [backupDay, setBackupDay] = useState(site.backupDay || 0);
  const [backupType, setBackupType] = useState(site.backupType || 'full');
  const [incrementalFullRecurrence, setIncrementalFullRecurrence] = useState(site.incrementalFullRecurrence || 'daily');
  const [incrementalRecurrence, setIncrementalRecurrence] = useState(site.incrementalRecurrence || 'hourly');
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [, setSettingsDirty] = useState(false);
  const [savingAndTriggering, setSavingAndTriggering] = useState(false);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'LIST_BACKUPS', siteId: site.id }
      });

      if (error) throw error;
      setBackups(data.backups || []);
    } catch (err: any) {
      console.error('Failed to fetch backups:', err);
      toast({
        title: 'Error',
        description: 'Failed to fetch backups from cloud storage.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();

    // Listen for backup status updates
    const channel = supabase
      .channel(`site-backups-${site.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workspace_sites',
          filter: `id=eq.${site.id}`,
        },
        (payload) => {
          const { backup_status, backup_last_run, backup_progress, backup_next_run, backup_next_full_run } = payload.new;
          if (onUpdateSite) {
            onUpdateSite({
              backup_status,
              backupLastRun: backup_last_run,
              backup_progress,
              backupNextRun: backup_next_run,
              backupNextFullRun: backup_next_full_run,
            });
          }
          // Keep local progress in sync
          if (typeof backup_progress === 'number') {
            setBackupProgress(backup_progress);
          }
          // If backup just finished, refresh the file list and reset progress
          if (backup_status === 'idle') {
            fetchBackups();
            setTimeout(() => setBackupProgress(0), 2000);
          }
          if (backup_status === 'failed') {
            setBackupProgress(0);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [site.id]);

  const handleManualBackup = async () => {
    try {
      setTriggering(true);

      // 1. Check storage limit first
      const { data: storageData } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'WORKSPACE_STORAGE', workspaceId: site.workspace_id }
      });
      
      if (storageData && storageData.used >= storageData.total) {
        toast({
          title: 'Storage Limit Reached',
          description: 'Your workspace backup storage limit (10 GB) has been reached. Please delete old backups to free up space.',
          variant: 'destructive',
        });
        return;
      }

      // 2. Trigger backup
      const isIncremental = backupType === 'incremental' && site.lastFullBackupKey;
      await callSiteApi(site.url, site.publicKey, site.secretKey, 'backups/trigger', 'POST', JSON.stringify({ type: isIncremental ? 'incremental' : 'full' }));
      
      if (onUpdateSite) onUpdateSite({ backup_status: 'backing_up' });
      setBackupProgress(0);

      toast({
        title: 'Backup Started',
        description: 'A new site backup has been scheduled and is running in the background.',
      });

    } catch (err: any) {
      toast({
        title: 'Backup Failed',
        description: err.message || 'Failed to trigger site backup.',
        variant: 'destructive',
      });
      if (onUpdateSite) onUpdateSite({ backup_status: 'idle' });
    } finally {
      setTriggering(false);
    }
  };

  const handleDownload = async (backupKey: string, fileName: string) => {
    try {
      setDownloading(backupKey);
      const { data, error } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'GET_DOWNLOAD_URL', key: backupKey, siteId: site.id }
      });

      if (error) throw error;
      if (!data.url) throw new Error('Failed to get download URL');

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = data.url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({ title: 'Download Started' });
    } catch (err: any) {
      toast({
        title: 'Download Failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setDownloading(null);
    }
  };

  const getKeysToRestore = (backup: Backup, scope: 'full' | 'files' | 'database') => {
    const keys: any[] = [];
    
    // For incremental file restore, we need the base full backup files first
    if (backup.type === 'incremental' && (scope === 'full' || scope === 'files')) {
      const baseFull = backups.find(b => b.type === 'full' && b.id < backup.id);
      if (baseFull?.files) {
        keys.push({ key: baseFull.files.key, scope: 'files' });
      } else if (baseFull?.legacy) {
        keys.push({ key: baseFull.legacy.key, scope: 'files' });
      }
    }

    if (scope === 'full') {
      if (backup.legacy) keys.push({ key: backup.legacy.key, scope: 'full' });
      if (backup.files) keys.push({ key: backup.files.key, scope: 'full' });
      if (backup.database) keys.push({ key: backup.database.key, scope: 'full' });
    } else if (scope === 'files') {
      if (backup.files) keys.push({ key: backup.files.key, scope: 'files' });
      else if (backup.legacy) keys.push({ key: backup.legacy.key, scope: 'files' });
    } else if (scope === 'database') {
      if (backup.database) keys.push({ key: backup.database.key, scope: 'database' });
      else if (backup.legacy) keys.push({ key: backup.legacy.key, scope: 'database' });
    }
    
    return keys;
  };

  const handleRestore = async (keysToRestore: any[]) => {
    if (!keysToRestore || keysToRestore.length === 0) return;

    if (!confirm('Are you sure you want to restore from this backup? This will overwrite existing files/database based on your selection.')) {
      return;
    }

    try {
      setRestoring(keysToRestore[0]?.key);
      if (onUpdateSite) onUpdateSite({ backup_status: 'restoring' });
      await callSiteApi(site.url, site.publicKey, site.secretKey, 'backups/restore', 'POST', JSON.stringify({ keys_to_restore: keysToRestore }));
      
      toast({
        title: 'Restoration Started',
        description: 'Site restoration is now running in the background. Please do not close the dashboard.',
      });
    } catch (err: any) {
      toast({
        title: 'Restoration Failed',
        description: err.message || 'Failed to trigger restoration.',
        variant: 'destructive',
      });
      if (onUpdateSite) onUpdateSite({ backup_status: 'idle' });
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (backupIds: string[], keysToDelete: string[]) => {
    const incrementalCount = backupIds.length - 1;
    const confirmMsg = incrementalCount > 0
      ? `This will permanently delete this full backup and its ${incrementalCount} incremental backup${incrementalCount > 1 ? 's' : ''} (${backupIds.length} total). Continue?`
      : 'Are you sure you want to permanently delete this backup?';

    if (!confirm(confirmMsg)) return;

    try {
      setIsDeleting(backupIds[0]);
      
      // Delete each file key associated with this backup group
      for (const key of keysToDelete) {
        if (!key) continue;
        const { error } = await supabase.functions.invoke('r2-backups', {
          body: { action: 'DELETE_BACKUP', key, siteId: site.id }
        });
        if (error) throw error;
      }
      
      setBackups(prev => prev.filter(b => !backupIds.includes(b.id)));
      toast({
        title: 'Backup Deleted',
        description: incrementalCount > 0
          ? `Removed 1 full backup and ${incrementalCount} incremental${incrementalCount > 1 ? 's' : ''}.`
          : 'Backup removed successfully.',
      });
    } catch (err: any) {
      toast({
        title: 'Delete Failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleResetStatus = async () => {
    if (!confirm('This will manually reset the backup indicator. Only do this if you are sure no backup is currently running.')) {
      return;
    }

    try {
      setUpdatingSettings(true);
      const { error } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'UPDATE_STATUS', status: 'idle', siteId: site.id }
      });

      if (error) throw error;
      
      if (onUpdateSite) onUpdateSite({ backup_status: 'idle' });
      toast({ title: 'Status Reset', description: 'Backup indicator has been reset to idle.' });
    } catch (err: any) {
      toast({
        title: 'Reset Failed',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingSettings(false);
    }
  };

  // Helper: compute both next run times independently
  const computeNextRuns = (type: string, rec: string, incRec: string, incFullRec: string, bTime: string, bDay: number): { nextIncrementalRun: string | null; nextFullRun: string | null } => {
    const now = new Date();
    const addMs = (ms: number) => new Date(now.getTime() + ms).toISOString();

    const getNextForInterval = (interval: string, time: string | null, day: number | null): string => {
      switch (interval) {
        case 'hourly': return addMs(60 * 60 * 1000);
        case '3_hours': return addMs(3 * 60 * 60 * 1000);
        case '6_hours': return addMs(6 * 60 * 60 * 1000);
        case '12_hours': return addMs(12 * 60 * 60 * 1000);
        case 'daily': {
          const next = new Date(now);
          if (time) { const [h, m] = time.split(':').map(Number); next.setHours(h, m, 0, 0); }
          if (next <= now) next.setDate(next.getDate() + 1);
          return next.toISOString();
        }
        case 'weekly': {
          const next = new Date(now);
          if (time) { const [h, m] = time.split(':').map(Number); next.setHours(h, m, 0, 0); }
          const targetDay = day ?? 0;
          let daysUntil = targetDay - next.getDay();
          if (daysUntil < 0 || (daysUntil === 0 && next <= now)) daysUntil += 7;
          next.setDate(next.getDate() + daysUntil);
          return next.toISOString();
        }
        case 'monthly': {
          const next = new Date(now);
          if (time) { const [h, m] = time.split(':').map(Number); next.setHours(h, m, 0, 0); }
          next.setDate(day ?? 1);
          if (next <= now) next.setMonth(next.getMonth() + 1);
          return next.toISOString();
        }
        default: return addMs(24 * 60 * 60 * 1000);
      }
    };

    if (type === 'full') {
      if (rec === 'manual') return { nextIncrementalRun: null, nextFullRun: null };
      return { nextIncrementalRun: null, nextFullRun: getNextForInterval(rec, bTime, bDay) };
    }

    // Incremental mode — return both independently
    const nextInc = getNextForInterval(incRec, null, null);
    const nextFull = getNextForInterval(incFullRec, bTime, bDay);
    return { nextIncrementalRun: nextInc, nextFullRun: nextFull };
  };

  // Save all settings at once + trigger initial full backup
  const handleSaveSettings = async () => {
    try {
      setSavingAndTriggering(true);
      setUpdatingSettings(true);

      // Determine if automatic scheduling is enabled
      const isAutomatic = backupType === 'incremental' || recurrence !== 'manual';

      // Compute both next run times independently
      const nextRuns = isAutomatic
        ? computeNextRuns(backupType, recurrence, incrementalRecurrence, incrementalFullRecurrence, backupTime, backupDay)
        : { nextIncrementalRun: null, nextFullRun: null };

      // For the legacy backup_next_run field, use the incremental time (or full if in full-only mode)
      const nextRun = nextRuns.nextIncrementalRun || nextRuns.nextFullRun;

      // Save all settings to the database
      const { error } = await supabase
        .from('workspace_sites')
        .update({
          backup_recurrence: recurrence,
          backup_retention: retention,
          backup_time: backupTime,
          backup_day: backupDay,
          backup_type: backupType,
          incremental_full_recurrence: incrementalFullRecurrence,
          incremental_recurrence: incrementalRecurrence,
          backup_enabled: isAutomatic,
          backup_next_run: nextRun,
          backup_next_full_run: nextRuns.nextFullRun,
        })
        .eq('id', site.id);

      if (error) throw error;

      // Update parent component state
      if (onUpdateSite) {
        onUpdateSite({
          backupRecurrence: recurrence,
          backupRetention: retention,
          backupTime: backupTime,
          backupDay: backupDay,
          backupType: backupType,
          incrementalFullRecurrence: incrementalFullRecurrence,
          incrementalRecurrence: incrementalRecurrence,
          backupEnabled: isAutomatic,
          backupNextRun: nextRun ?? undefined,
          backupNextFullRun: nextRuns.nextFullRun ?? undefined,
        });
      }

      setSettingsDirty(false);

      // Check storage limit
      const { data: storageData } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'WORKSPACE_STORAGE', workspaceId: site.workspace_id }
      });
      
      if (storageData && storageData.used >= storageData.total) {
        toast({
          title: 'Settings Saved — Storage Full',
          description: 'Backup settings were saved, but your storage limit is full. Delete old backups to enable automatic backups.',
          variant: 'destructive',
        });
        return;
      }

      // Trigger an initial full base backup
      toast({
        title: 'Settings Saved',
        description: isAutomatic
          ? 'Backup settings saved. Starting initial full backup now…'
          : 'Backup settings saved.',
      });

      if (isAutomatic) {
        // Fire the initial full base backup
        await callSiteApi(site.url, site.publicKey, site.secretKey, 'backups/trigger', 'POST', JSON.stringify({ type: 'full' }));
        if (onUpdateSite) onUpdateSite({ backup_status: 'backing_up' });
        setBackupProgress(0);
      }

    } catch (err: any) {
      toast({
        title: 'Failed to Save Settings',
        description: err.message || 'An error occurred while saving backup settings.',
        variant: 'destructive',
      });
    } finally {
      setSavingAndTriggering(false);
      setUpdatingSettings(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const groupedBackups = React.useMemo(() => {
    const groups: { base: Backup, incrementals: Backup[] }[] = [];
    let currentGroup: { base: Backup | null, incrementals: Backup[] } | null = null;

    for (const b of backups) {
      if (b.type === 'incremental') {
        if (!currentGroup) currentGroup = { base: null, incrementals: [] };
        currentGroup.incrementals.push(b);
      } else {
        if (!currentGroup) {
          groups.push({ base: b, incrementals: [] });
        } else {
          currentGroup.base = b;
          groups.push(currentGroup as { base: Backup, incrementals: Backup[] });
          currentGroup = null;
        }
      }
    }
    
    if (currentGroup && currentGroup.base === null) {
      currentGroup.incrementals.forEach(inc => {
        groups.push({ base: inc, incrementals: [] });
      });
    }

    return groups;
  }, [backups]);

  const renderActions = (backup: Backup, isSubItem: boolean, groupIdsToDelete: string[], groupKeysToDelete: string[]) => {
    return (
      <div className="flex items-center gap-4 mt-4 xl:mt-0">
        {/* Download Group */}
        <div className="flex items-center">
          {backup.files && backup.database ? (
            <div className={`flex items-center rounded-lg shadow-sm shadow-blue-500/10 ${isSubItem ? 'scale-90 origin-right' : ''}`}>
              <button onClick={() => {
                handleDownload(backup.files!.key, backup.name + '-files.zip');
                setTimeout(() => handleDownload(backup.database!.key, backup.name + '-database.zip'), 500);
              }} disabled={!!downloading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg bg-blue-500 text-white font-bold text-xs hover:bg-blue-600 transition-all disabled:opacity-50">
                {downloading === backup.files?.key || downloading === backup.database?.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} {isSubItem ? 'Download' : 'Download Full'}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger disabled={!!downloading} className="flex items-center justify-center px-2 py-1.5 rounded-r-lg bg-blue-600 border-l border-blue-400 text-white hover:bg-blue-700 transition-all disabled:opacity-50">
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => handleDownload(backup.files!.key, backup.name + '-files.zip')} className="cursor-pointer text-xs font-bold gap-2">
                    <HardDrive className="w-3.5 h-3.5" /> Site Files Only
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownload(backup.database!.key, backup.name + '-database.zip')} className="cursor-pointer text-xs font-bold gap-2">
                    <Database className="w-3.5 h-3.5" /> Database Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : backup.legacy || backup.files || backup.database ? (
            <button onClick={() => handleDownload((backup.legacy || backup.files || backup.database)!.key, backup.name + '.zip')} disabled={!!downloading} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 text-white font-bold text-xs hover:bg-blue-600 shadow-sm shadow-blue-500/20 transition-all disabled:opacity-50 ${isSubItem ? 'scale-90 origin-right' : ''}`}>
              {downloading === (backup.legacy || backup.files || backup.database)?.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download Zip
            </button>
          ) : null}
        </div>

        {/* Restore Group */}
        <div className="flex items-center">
          {backup.files && backup.database || backup.legacy ? (
            <div className={`flex items-center rounded-lg shadow-sm shadow-emerald-500/10 ${isSubItem ? 'scale-90 origin-right' : ''}`}>
              <button onClick={() => handleRestore(getKeysToRestore(backup, 'full'))} disabled={!!restoring} className="flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg bg-emerald-500 text-white font-bold text-xs hover:bg-emerald-600 transition-all disabled:opacity-50">
                {restoring === (backup.files?.key || backup.legacy?.key) ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {isSubItem ? 'Restore' : 'Restore Full'}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger disabled={!!restoring} className="flex items-center justify-center px-2 py-1.5 rounded-r-lg bg-emerald-600 border-l border-emerald-400 text-white hover:bg-emerald-700 transition-all disabled:opacity-50">
                  <ChevronDown className="w-3 h-3" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem onClick={() => handleRestore(getKeysToRestore(backup, 'files'))} className="cursor-pointer text-xs font-bold gap-2">
                    <HardDrive className="w-3.5 h-3.5" /> Site Files Only
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleRestore(getKeysToRestore(backup, 'database'))} className="cursor-pointer text-xs font-bold gap-2">
                    <Database className="w-3.5 h-3.5" /> Database Only
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : backup.files ? (
            <button onClick={() => handleRestore(getKeysToRestore(backup, 'files'))} disabled={!!restoring} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white font-bold text-xs hover:bg-emerald-600 shadow-sm shadow-emerald-500/20 transition-all disabled:opacity-50 ${isSubItem ? 'scale-90 origin-right' : ''}`}>
              {restoring === backup.files.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Restore Files
            </button>
          ) : backup.database ? (
            <button onClick={() => handleRestore(getKeysToRestore(backup, 'database'))} disabled={!!restoring} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white font-bold text-xs hover:bg-emerald-600 shadow-sm shadow-emerald-500/20 transition-all disabled:opacity-50 ${isSubItem ? 'scale-90 origin-right' : ''}`}>
              {restoring === backup.database.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Restore DB
            </button>
          ) : null}
        </div>

        <div className="w-px h-6 bg-border/50 mx-1"></div>

        <button 
          onClick={() => handleDelete(groupIdsToDelete, groupKeysToDelete)}
          disabled={!!isDeleting}
          className="p-2.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all active:scale-90 disabled:opacity-50"
          title={isSubItem ? "Delete Backup" : "Delete Backup Group"}
        >
          {isDeleting === groupIdsToDelete[0] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 rounded-2xl bg-workspace-accent/10 flex items-center justify-center">
            <Database className="w-6 h-6 text-workspace-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Site & Database Backups</h1>
            <p className="text-sm text-muted-foreground">Manage storage, scheduling, and site restorations from your secure cloud.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setSettingsOpen(!settingsOpen)}
            className={"p-2.5 rounded-xl border transition-all " + (settingsOpen ? 'bg-workspace-accent/10 border-workspace-accent text-workspace-accent' : 'bg-card border-border hover:bg-muted')}
          >
            <SettingsIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleManualBackup}
            disabled={triggering || site.backup_status === 'backing_up' || site.backup_status === 'restoring'}
            className="flex items-center space-x-2 px-6 py-2.5 bg-workspace-accent text-white rounded-xl shadow-lg hover:bg-workspace-accent/90 transition-all disabled:opacity-50"
          >
            { (triggering || site.backup_status === 'backing_up') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
            <span className="font-bold">
              {site.backup_status === 'backing_up' ? 'Backing Up...' : 
               site.backup_status === 'restoring' ? 'Restoring...' : 
               backupType === 'incremental' ? (site.lastFullBackupKey ? 'Create Incremental Backup' : 'Create Base Backup') : 'Create Backup'}
            </span>
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {settingsOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-6 bg-card border border-border rounded-2xl shadow-sm mb-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="space-y-3">
                  <label className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Backup Type
                  </label>
                  <div className="flex bg-muted/50 p-1 rounded-lg border border-border w-full">
                    <button
                      onClick={() => { setBackupType('full'); setSettingsDirty(true); }}
                      disabled={updatingSettings}
                      className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${backupType === 'full' ? 'bg-workspace-accent text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Full
                    </button>
                    <button
                      onClick={() => { setBackupType('incremental'); setSettingsDirty(true); }}
                      disabled={updatingSettings}
                      className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${backupType === 'incremental' ? 'bg-workspace-accent text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      Incremental
                    </button>
                  </div>
                </div>

                {backupType === 'full' ? (
                  <div className="space-y-3">
                    <label className="text-sm font-bold text-foreground flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Backup Recurrence
                    </label>
                    <div className="flex bg-muted/50 p-1 rounded-lg border border-border w-full">
                      <button
                        onClick={() => { setRecurrence('manual'); setSettingsDirty(true); }}
                        disabled={updatingSettings}
                        className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${recurrence === 'manual' ? 'bg-workspace-accent text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Manual
                      </button>
                      <button
                        onClick={() => {
                          if (recurrence === 'manual') { setRecurrence('daily'); setSettingsDirty(true); }
                        }}
                        disabled={updatingSettings}
                        className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${recurrence !== 'manual' ? 'bg-workspace-accent text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Automatic
                      </button>
                    </div>

                    {recurrence !== 'manual' && (
                      <div className="space-y-4 pt-2">
                        <div className="space-y-3">
                          <label className="text-xs font-bold text-foreground">Interval</label>
                          <select
                            value={recurrence}
                            onChange={(e) => { setRecurrence(e.target.value); setSettingsDirty(true); }}
                            disabled={updatingSettings}
                            className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                          >
                            <option className="bg-card text-foreground" value="hourly">Hourly</option>
                            <option className="bg-card text-foreground" value="3_hours">Every 3 Hours</option>
                            <option className="bg-card text-foreground" value="6_hours">Every 6 Hours</option>
                            <option className="bg-card text-foreground" value="12_hours">Every 12 Hours</option>
                            <option className="bg-card text-foreground" value="daily">Daily</option>
                            <option className="bg-card text-foreground" value="weekly">Weekly</option>
                            <option className="bg-card text-foreground" value="monthly">Monthly</option>
                          </select>
                        </div>

                        {recurrence !== 'hourly' && recurrence !== '3_hours' && recurrence !== '6_hours' && recurrence !== '12_hours' && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              <label className="text-xs font-bold text-foreground">Time of Day</label>
                              <input 
                                type="time" 
                                value={backupTime}
                                onChange={(e) => { setBackupTime(e.target.value); setSettingsDirty(true); }}
                                disabled={updatingSettings}
                                className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                              />
                            </div>
                            {recurrence === 'weekly' && (
                              <div className="space-y-3">
                                <label className="text-xs font-bold text-foreground">Day of Week</label>
                                <select 
                                  value={backupDay}
                                  onChange={(e) => { setBackupDay(parseInt(e.target.value)); setSettingsDirty(true); }}
                                  disabled={updatingSettings}
                                  className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                                >
                                  <option className="bg-card text-foreground" value={0}>Sunday</option>
                                  <option className="bg-card text-foreground" value={1}>Monday</option>
                                  <option className="bg-card text-foreground" value={2}>Tuesday</option>
                                  <option className="bg-card text-foreground" value={3}>Wednesday</option>
                                  <option className="bg-card text-foreground" value={4}>Thursday</option>
                                  <option className="bg-card text-foreground" value={5}>Friday</option>
                                  <option className="bg-card text-foreground" value={6}>Saturday</option>
                                </select>
                              </div>
                            )}
                            {recurrence === 'monthly' && (
                              <div className="space-y-3">
                                <label className="text-xs font-bold text-foreground">Day of Month</label>
                                <select 
                                  value={backupDay}
                                  onChange={(e) => { setBackupDay(parseInt(e.target.value)); setSettingsDirty(true); }}
                                  disabled={updatingSettings}
                                  className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                                >
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                    <option className="bg-card text-foreground" key={day} value={day}>{day}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <label className="text-sm font-bold text-foreground flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Full Backup Interval
                      </label>
                      <select
                        value={incrementalFullRecurrence}
                        onChange={(e) => { setIncrementalFullRecurrence(e.target.value); setSettingsDirty(true); }}
                        disabled={updatingSettings}
                        className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                      >
                        <option className="bg-card text-foreground" value="daily">Daily</option>
                        <option className="bg-card text-foreground" value="weekly">Weekly</option>
                        <option className="bg-card text-foreground" value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <label className="text-xs font-bold text-foreground">Time of Day</label>
                        <input 
                          type="time" 
                          value={backupTime}
                          onChange={(e) => { setBackupTime(e.target.value); setSettingsDirty(true); }}
                          disabled={updatingSettings}
                          className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                        />
                      </div>
                      {incrementalFullRecurrence === 'weekly' && (
                        <div className="space-y-3">
                          <label className="text-xs font-bold text-foreground">Day of Week</label>
                          <select 
                            value={backupDay}
                            onChange={(e) => { setBackupDay(parseInt(e.target.value)); setSettingsDirty(true); }}
                            disabled={updatingSettings}
                            className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                          >
                            <option className="bg-card text-foreground" value={0}>Sunday</option>
                            <option className="bg-card text-foreground" value={1}>Monday</option>
                            <option className="bg-card text-foreground" value={2}>Tuesday</option>
                            <option className="bg-card text-foreground" value={3}>Wednesday</option>
                            <option className="bg-card text-foreground" value={4}>Thursday</option>
                            <option className="bg-card text-foreground" value={5}>Friday</option>
                            <option className="bg-card text-foreground" value={6}>Saturday</option>
                          </select>
                        </div>
                      )}
                      {incrementalFullRecurrence === 'monthly' && (
                        <div className="space-y-3">
                          <label className="text-xs font-bold text-foreground">Day of Month</label>
                          <select 
                            value={backupDay}
                            onChange={(e) => { setBackupDay(parseInt(e.target.value)); setSettingsDirty(true); }}
                            disabled={updatingSettings}
                            className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                          >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                              <option className="bg-card text-foreground" key={day} value={day}>{day}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 pt-2">
                      <label className="text-sm font-bold text-foreground flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Incremental Interval
                      </label>
                      <select
                        value={incrementalRecurrence}
                        onChange={(e) => { setIncrementalRecurrence(e.target.value); setSettingsDirty(true); }}
                        disabled={updatingSettings}
                        className="w-full h-9 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                      >
                        <option className="bg-card text-foreground" value="hourly">Hourly</option>
                        <option className="bg-card text-foreground" value="3_hours">Every 3 Hours</option>
                        <option className="bg-card text-foreground" value="6_hours">Every 6 Hours</option>
                        <option className="bg-card text-foreground" value="12_hours">Every 12 Hours</option>
                        <option className="bg-card text-foreground" value="daily">Daily</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="text-sm font-bold text-foreground flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Retention Policy
                  </label>
                  <select
                    value={retention}
                    onChange={(e) => { setRetention(parseInt(e.target.value)); setSettingsDirty(true); }}
                    disabled={updatingSettings}
                    className="w-full h-10 px-3 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-workspace-accent"
                  >
                    <option className="bg-card text-foreground" value={7}>Keep backups for the last 7 days</option>
                    <option className="bg-card text-foreground" value={14}>Keep backups for the last 14 days</option>
                    <option className="bg-card text-foreground" value={30}>Keep backups for the last 30 days</option>
                    <option className="bg-card text-foreground" value={60}>Keep backups for the last 60 days</option>
                  </select>
                </div>

              <div className="space-y-4">
                 <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] leading-relaxed text-blue-700 dark:text-blue-400 font-medium">
                        Backups are stored on our secure cloud storage. Restoring will temporarily put your site in maintenance mode.
                      </p>
                    </div>
                 </div>

                 {/* Save Settings Button */}
                 <button
                   onClick={handleSaveSettings}
                   disabled={savingAndTriggering || site.backup_status === 'backing_up'}
                   className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-lg disabled:opacity-50 bg-workspace-accent text-white hover:bg-workspace-accent/90"
                 >
                   {savingAndTriggering ? (
                     <><Loader2 className="w-4 h-4 animate-spin" /> Saving & Starting Backup…</>
                   ) : (
                     <><Save className="w-4 h-4" /> Save Settings & Start Backup</>
                   )}
                 </button>
                 {(backupType === 'incremental' || (backupType === 'full' && recurrence !== 'manual')) && (
                   <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                     <Zap className="w-4 h-4 text-emerald-500 shrink-0" />
                     <p className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium">
                       Saving will trigger an initial full backup. Subsequent automatic backups will run on the schedule above.
                     </p>
                   </div>
                 )}
              </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backup Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border p-4 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <span className="text-xs font-bold text-muted-foreground">Storage Health</span>
          </div>
          <div className="text-xl font-bold text-foreground">{formatSize(backups.reduce((sum, b) => sum + (b.totalSize || 0), 0))}</div>
          <p className="text-[10px] text-muted-foreground mt-1">Total space utilized by this site's backups.</p>
        </div>

        <div className="bg-card border border-border p-4 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
              <HardDrive className="w-4 h-4" />
            </div>
            <span className="text-xs font-bold text-muted-foreground">Total Backups</span>
          </div>
          <div className="text-xl font-bold text-foreground">{backups.length} Archives</div>
          <p className="text-[10px] text-muted-foreground mt-1">Within retention limit of {retention}.</p>
        </div>

        <div className="bg-card border border-border p-4 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-workspace-accent/10 flex items-center justify-center text-workspace-accent">
              <Clock className="w-4 h-4" />
            </div>
            <span className="text-xs font-bold text-muted-foreground">Last Action</span>
          </div>
          <div className="text-xl font-bold text-foreground truncate flex items-center gap-2">
            {(triggering || site.backup_status === 'backing_up') && (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-workspace-accent" />
                <button 
                  onClick={handleResetStatus}
                  className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 font-bold uppercase hover:bg-amber-200 transition-colors"
                  title="Force reset stuck status"
                >
                  Reset
                </button>
              </div>
            )}
            {site.backupLastRun ? new Date(site.backupLastRun).toLocaleDateString() : 'No activity'}
            {site.backup_status === 'failed' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-bold uppercase ml-2 italic">Failed</span>}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Automatic sync {recurrence === 'manual' ? 'off' : 'every ' + recurrence}.</p>
        </div>

        <div className="bg-card border border-border p-4 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500">
              <Calendar className="w-4 h-4" />
            </div>
            <span className="text-xs font-bold text-muted-foreground">Upcoming Backup</span>
          </div>
          <div className="text-xl font-bold text-foreground truncate">
            {site.backupNextRun ? new Date(site.backupNextRun).toLocaleDateString() : 'Not scheduled'}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            {site.backupNextRun ? new Date(site.backupNextRun).toLocaleTimeString() : 'Automatic backups off'}
          </p>
        </div>
      </div>

      {/* Live Progress Bar — shown only when backup/restore is running */}
      <AnimatePresence>
        {(site.backup_status === 'backing_up' || site.backup_status === 'restoring') && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="bg-card border border-border rounded-2xl p-5 shadow-sm overflow-hidden relative"
          >
            {/* Animated shimmer background */}
            <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-workspace-accent/5 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: 'linear' }}
              />
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-workspace-accent/15 flex items-center justify-center">
                    <Loader2 className="w-3.5 h-3.5 text-workspace-accent animate-spin" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">
                      {site.backup_status === 'restoring' ? 'Restoring Site…' : 'Backup In Progress'}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {site.backup_status === 'restoring'
                        ? 'Extracting and applying backup. Please do not edit the site.'
                        : 'Streaming site files and database securely to cloud storage.'}
                    </p>
                  </div>
                </div>
                <span className="text-2xl font-black tabular-nums text-workspace-accent">
                  {site.backup_status === 'restoring' ? '—' : `${backupProgress}%`}
                </span>
              </div>

              {/* Progress bar — only for backups, not restores */}
              {site.backup_status !== 'restoring' && (
                <>
                  <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-workspace-accent"
                      style={{ boxShadow: '0 0 12px color-mix(in srgb, var(--workspace-accent) 50%, transparent)' }}
                      initial={{ width: '0%' }}
                      animate={{ width: `${backupProgress}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground font-medium">
                      {backupProgress < 15 ? 'Counting files…' : backupProgress < 85 ? 'Packaging files…' : backupProgress < 95 ? 'Finalizing upload…' : 'Completing…'}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-medium">You can safely browse other tabs.</span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backup List */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-border bg-muted/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
             <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Backups Timeline</span>
          </div>
          <button onClick={fetchBackups} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <RefreshCw className={"w-3.5 h-3.5 " + (loading ? 'animate-spin' : '')} />
          </button>
        </div>

        <div className="divide-y divide-border">
          {loading && backups.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center">
              <Loader2 className="w-10 h-10 animate-spin text-workspace-accent mb-4" />
              <p className="text-sm font-medium text-muted-foreground">Scanning cloud storage for backups...</p>
            </div>
          ) : backups.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-center px-6">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Database className="w-8 h-8 text-muted-foreground/30" />
              </div>
              <h3 className="text-lg font-bold text-foreground">No Backups Found</h3>
              <p className="text-sm text-muted-foreground mt-2 max-w-sm">No backup archives were found for this site. Create your first manual backup now.</p>
              <button 
                onClick={handleManualBackup} 
                className="mt-6 px-6 py-2 bg-workspace-accent text-white rounded-xl shadow-lg hover:bg-workspace-accent/90 transition-all font-bold"
              >
                Create First Backup
              </button>
            </div>
          ) : (
            groupedBackups.map((group, index) => {
              const backup = group.base;
              const hasIncrementals = group.incrementals.length > 0;
              const isExpanded = expandedGroups.has(backup.id);
              
              const totalGroupSize = backup.totalSize + group.incrementals.reduce((sum, inc) => sum + (inc.totalSize || 0), 0);
              const totalFilesSize = (backup.files?.size || 0) + group.incrementals.reduce((sum, inc) => sum + (inc.files?.size || 0), 0);
              const totalDbSize = (backup.database?.size || 0) + group.incrementals.reduce((sum, inc) => sum + (inc.database?.size || 0), 0);

              const allBackupIds = [backup.id, ...group.incrementals.map(inc => inc.id)];
              const allKeysToDelete = [
                backup.legacy?.key, backup.files?.key, backup.database?.key,
                ...group.incrementals.flatMap(inc => [inc.legacy?.key, inc.files?.key, inc.database?.key])
              ].filter(Boolean) as string[];

              return (
                <div key={`group_${backup.id}_${index}`} className="flex flex-col border-b border-border last:border-0 group">
                  <div className="p-4 md:p-6 hover:bg-muted/10 transition-colors flex flex-col gap-4">
                    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        {hasIncrementals ? (
                          <button 
                            onClick={() => toggleGroup(backup.id)}
                            className="w-12 h-12 rounded-xl bg-muted hover:bg-workspace-accent/10 flex items-center justify-center transition-colors shadow-sm border border-border shrink-0"
                          >
                            {isExpanded ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronRight className="w-5 h-5 text-muted-foreground" />}
                          </button>
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-muted group-hover:bg-workspace-accent/5 flex items-center justify-center transition-colors shrink-0">
                            <Database className="w-6 h-6 text-muted-foreground group-hover:text-workspace-accent/50" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-foreground">{backup.name}</span>
                            {hasIncrementals ? (
                              <span onClick={() => toggleGroup(backup.id)} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 cursor-pointer hover:bg-emerald-200 transition-colors">
                                Full Incremental ({group.incrementals.length})
                              </span>
                            ) : backup.type === 'incremental' ? (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">Incremental</span>
                            ) : backup.type === 'legacy' ? (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">Legacy</span>
                            ) : (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">Full</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground font-medium">
                            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(backup.lastModified).toLocaleString()}</span>
                            <span className="flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5" /> {formatSize(totalGroupSize)}</span>
                            
                            {backup.files && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded border border-border">Files: {formatSize(totalFilesSize)}</span>}
                            {backup.database && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded border border-border">DB: {formatSize(totalDbSize)}</span>}
                          </div>
                        </div>
                      </div>

                      {renderActions(backup, false, allBackupIds, allKeysToDelete)}
                    </div>
                  </div>

                  {/* Incremental Accordion */}
                  <AnimatePresence>
                    {isExpanded && hasIncrementals && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden bg-muted/5 border-t border-border"
                      >
                        <div className="flex flex-col divide-y divide-border">
                          {group.incrementals.map((inc, incIndex) => (
                            <div key={`inc_${inc.id}_${incIndex}`} className="p-4 md:py-4 md:pr-6 md:pl-20 hover:bg-muted/10 transition-colors flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                                  <Database className="w-4 h-4 text-amber-500" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-foreground">{inc.name}</span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">Incremental</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-muted-foreground font-medium">
                                    <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(inc.lastModified).toLocaleString()}</span>
                                    <span className="flex items-center gap-1.5"><HardDrive className="w-3 h-3" /> {formatSize(inc.totalSize)}</span>
                                  </div>
                                </div>
                              </div>

                              {renderActions(inc, true, [inc.id], [inc.legacy?.key, inc.files?.key, inc.database?.key].filter(Boolean) as string[])}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
